import { z } from 'zod';

export const TASK_STATUS = ['backlog', 'todo', 'doing', 'blocked', 'done', 'archived'] as const;
export const TASK_PRIORITY = ['low', 'med', 'high', 'urgent'] as const;

export const TASK_STATUS_LABEL: Record<(typeof TASK_STATUS)[number], string> = {
  backlog: 'Pendientes',
  todo: 'Por hacer',
  doing: 'En curso',
  blocked: 'Bloqueada',
  done: 'Completada',
  archived: 'Archivada',
};

export const TASK_PRIORITY_LABEL: Record<(typeof TASK_PRIORITY)[number], string> = {
  low: 'Baja',
  med: 'Media',
  high: 'Alta',
  urgent: 'Urgente',
};

// Estados visibles en el Kanban (sin archived; con done en la última col).
export const KANBAN_COLUMNS = ['backlog', 'todo', 'doing', 'blocked', 'done'] as const;

export const TASK_SCOPE = ['team', 'personal'] as const;
export type TaskScope = (typeof TASK_SCOPE)[number];

export const TASK_SCOPE_LABEL: Record<TaskScope, string> = {
  team: 'Equipo',
  personal: 'Personal',
};

/*
 * Validación condicional: si scope='personal', area_id es opcional/null;
 * si scope='team', area_id es uuid obligatorio. Refinement en zod abajo.
 */
export const taskFormSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(2, 'Mínimo 2 caracteres')
      .max(200, 'Máximo 200 caracteres'),
    description: z
      .string()
      .trim()
      .max(5000)
      .optional()
      .or(z.literal('')),
    scope: z.enum(TASK_SCOPE),
    area_id: z
      .string()
      .uuid()
      .or(z.literal(''))
      .nullable()
      .optional(),
    status: z.enum(TASK_STATUS),
    priority: z.enum(TASK_PRIORITY),
    /*
     * Fecha + hora de vencimiento. <input type="datetime-local"> entrega
     * "YYYY-MM-DDTHH:mm" (sin timezone). Lo aceptamos como string opcional;
     * el caller lo convierte a ISO con new Date(v).toISOString() antes de
     * mandar al backend (timestamptz).
     */
    due_at: z
      .string()
      .trim()
      .optional()
      .or(z.literal('')),
    // Vínculo opcional con un prospecto/cliente.
    prospect_id: z.string().uuid().nullable().optional(),
    // Vínculo opcional con un proyecto (Anexo B workspace).
    project_id: z.string().uuid().nullable().optional(),
    // Vínculo opcional con una tarea padre (esta será sub-tarea de esa).
    parent_task_id: z.string().uuid().nullable().optional(),
  })
  .refine(
    (v) => v.scope === 'personal' || (typeof v.area_id === 'string' && v.area_id.length > 0),
    {
      path: ['area_id'],
      message: 'Elegí un área (obligatorio para tareas de equipo)',
    },
  );

export type TaskFormValues = z.infer<typeof taskFormSchema>;
